import React, { useCallback, useMemo } from 'react';
import SimpleCard from '../SimpleCard/SimpleCard';
import useTheme from '../style/useTheme';
import Typography, { TypographyColor } from '../Typography/Typography';
import { hex2rgba } from '../utils';

export type Props = React.PropsWithChildren<{
  eventId?: string;
  onClick?: () => void;
  sx?: React.CSSProperties;
  selected?: boolean;
  disabled?: boolean;
  'data-testid'?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  color?: TypographyColor;
}>;

const CardButton: React.FC<Props> = ({
  eventId,
  onClick,
  children,
  selected,
  disabled,
  sx,
  color = 'light',
  'data-testid': dataTestId = 'CardButton',
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
}) => {
  const handleClick = useCallback(() => {
    if (onClick) {
      onClick();
    }
  }, [onClick]);

  const { theme, resolveColor } = useTheme();

  const focusColor = useMemo(
    () => resolveColor(theme.components.card['focus-color']),
    [resolveColor, theme.components.card]
  );

  const disabledColor = useMemo(
    () => resolveColor(theme.components.card['disabled-color']),
    [resolveColor, theme.components.card]
  );

  return (
    <SimpleCard
      tabIndex={disabled ? undefined : 0}
      data-testid={dataTestId}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      sx={{
        padding: '24px 16px',
        boxSizing: 'border-box',
        ...(disabled && {
          border: `1px solid ${hex2rgba(disabledColor, 0.5)}`,
        }),
        ...(!disabled && {
          cursor: 'pointer',
          '&:hover': {
            border: `1px solid ${focusColor}`,
          },
          '&:focus': {
            border: `1px solid ${focusColor}`,
            boxShadow: `0px 0px 0px 4px ${hex2rgba(focusColor, 0.15)}`,
            // Remove default focus outline
            outline: 'none',
          },
        }),
        ...(selected && {
          border: `2px solid ${focusColor} !important`,
          boxShadow: `0px 0px 0px 4px ${hex2rgba(focusColor, 0.15)}`,
        }),
        ...sx,
      }}
      eventId={eventId}
      {...(!disabled && { onClick: handleClick })}
    >
      <Typography
        sx={{
          ...(disabled && { color: hex2rgba(disabledColor, 0.5) }),
        }}
        align="center"
        variant="label"
        color={color}
      >
        {children}
      </Typography>
    </SimpleCard>
  );
};

export default CardButton;
